import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import Admin from './pages/Admin';
import UserManagement from './pages/UserManagement';
import LoginHistory from './pages/LoginHistory';
import CacheAndMaintenance from './pages/CacheAndMaintenance';
import Invite from './pages/Invite';
import ProtectedRoute from './components/ProtectedRoute'; // Ensure the correct path
import { RoleRedirect } from './components/RoleRedirect';
import { AccessDenied } from './components/AccessDenied';
import { useAuthInterceptor } from './hooks/useAuthInterceptor';

const SSO_REAUTH_QUERY_PARAM = 'sso_reauth';
const SSO_RETURN_TO_QUERY_PARAM = 'sso_return_to';

function App() {
  const { isLoading, isAuthenticated, loginWithRedirect, user, getAccessTokenSilently } = useAuth0();
  const [accessToken, setAccessToken] = useState('');
  const isInviteRoute = window.location.pathname === '/invite';

  // Initialize auth interceptor for automatic logout on token expiration
  useAuthInterceptor();

  useEffect(() => {
    const fetchAccessToken = async () => {
      const token = await getAccessTokenSilently();
      setAccessToken(token);
    };

    if (!isLoading && isAuthenticated && !accessToken) {
      fetchAccessToken();
    }

    if (!isLoading && !isAuthenticated && !isInviteRoute) {
      const url = new URL(window.location.href);
      const isLogoutReauth = url.searchParams.get(SSO_REAUTH_QUERY_PARAM) === '1';
      const requestedReturnTo = url.searchParams.get(SSO_RETURN_TO_QUERY_PARAM);
      const defaultReturnTo = `${window.location.pathname}${window.location.search}`;
      const returnTo = isLogoutReauth && requestedReturnTo ? decodeURIComponent(requestedReturnTo) : defaultReturnTo;

      loginWithRedirect({
        appState: {
          returnTo,
        },
      });
    }
  }, [isLoading, isAuthenticated, isInviteRoute, accessToken, loginWithRedirect, user, getAccessTokenSilently]);

  if (!isAuthenticated && !isInviteRoute) {
    return <div></div>;
  }

  return (
    <>
      <Router>
        <Routes>
          <Route path="/invite" element={<Invite />} />
          <Route path="/" element={<RoleRedirect />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRole="Admin">
                <Admin user={user} accessToken={accessToken} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-management"
            element={
              <ProtectedRoute requiredRole="Admin">
                <UserManagement user={user} accessToken={accessToken} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/login-history"
            element={
              <ProtectedRoute requiredRole="Admin">
                <LoginHistory user={user} accessToken={accessToken} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cache-maintenance"
            element={
              <ProtectedRoute requiredRole="Admin">
                <CacheAndMaintenance user={user} accessToken={accessToken} />
              </ProtectedRoute>
            }
          />
          <Route path="/access-denied" element={<AccessDenied />} />
        </Routes>
      </Router>
    </>
  );
}

export default App;
